import { useUserSearch } from "@/services/userManageService";
import { SearchQuery } from "@/types/common";
import { UserDepartmentPositionResponse, UserSearchCondition } from "@/types/user";
import { Inbox as InboxIcon, Close as CloseIcon } from "@mui/icons-material";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { DataGrid, GridColDef, GridOverlay, GridPaginationModel, GridRowSelectionModel } from "@mui/x-data-grid";
import React, { useEffect, useRef, useState } from "react";
import Draggable from "react-draggable";
import Paper from "@mui/material/Paper";

interface UserSearchDialogProps {
  open: boolean;
  onClose: () => void;
  onApply: (selectedUsers: UserDepartmentPositionResponse[]) => void;
  localUsers: UserDepartmentPositionResponse[];
}

const DraggablePaper = React.forwardRef<HTMLDivElement, any>((props, ref) => {
  const nodeRef = React.useRef<HTMLDivElement>(null);
  React.useImperativeHandle(ref, () => nodeRef.current as HTMLDivElement);

  return (
    <Draggable handle='#draggable-department-user-search-title' nodeRef={nodeRef}>
      <Paper {...props} ref={nodeRef} />
    </Draggable>
  );
});

const UserSearchDialog: React.FC<UserSearchDialogProps> = ({ open, onClose, onApply, localUsers }) => {
  const paperRef = useRef<HTMLDivElement>(null);
  const [searchQuery, setSearchQuery] = useState<SearchQuery<UserSearchCondition>>({ page: 0, size: 15 });
  const [selectedRows, setSelectedRows] = useState<GridRowSelectionModel>([]);
  const [localSearch, setLocalSearch] = useState<UserSearchCondition>({ roleId: null, keyword: "" });
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: searchQuery.page,
    pageSize: searchQuery.size,
  });

  const { data, isFetching } = useUserSearch(searchQuery);
  const rows = (data?.items ?? []).map((row) => ({
    ...row,
    id: row.id ?? "",
    roleId: row.roleId ?? "",
  }));

  useEffect(() => {
    setSearchQuery((prev) => {
      if (prev.page === paginationModel.page && prev.size === paginationModel.pageSize) {
        return prev;
      }
      return { ...prev, page: paginationModel.page, size: paginationModel.pageSize };
    });
  }, [paginationModel]);

  useEffect(() => {
    setPaginationModel({ page: searchQuery.page, pageSize: searchQuery.size });
  }, [searchQuery.page, searchQuery.size]);

  const columns: GridColDef[] = [
    { field: "name", headerName: "이름", headerAlign: "center", minWidth: 150 },
    { field: "accountId", headerName: "계정 아이디", headerAlign: "center", minWidth: 200 },
    { field: "email", headerName: "이메일", headerAlign: "center", flex: 1 },
  ];

  const handleSearchButtonClick: () => void = () => {
    if (localSearch.keyword && (localSearch.keyword.length < 2 || localSearch.keyword.length > 20)) {
      alert("통합 검색어는 최소 2자이상 20자이하로 입력해주세요.");
      return;
    }
    setSearchQuery((prev) => ({
      ...prev,
      page: 0,
      conditions: { ...localSearch },
    }));
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleSearchButtonClick();
    }
  };

  const handleApply = () => {
    const selectedUsers = rows.filter((row) => selectedRows.includes(row.id));
    const minId =
      localUsers.length > 0 ? Math.min(...localUsers.map((u) => (typeof u.id === "number" ? u.id : 0)), 0) : 0;

    const mappedUsers: UserDepartmentPositionResponse[] = selectedUsers.map((row, idx) => ({
      id: minId - idx - 1,
      userId: row.id,
      departmentId: null,
      userName: row.name,
      userAccountId: row.accountId,
      userEmail: row.email,
      userPositionId: null,
      userPositionName: null,
      isHead: false,
    }));

    onApply(mappedUsers);
    handleDialogClose();
  };

  const handleDialogClose = () => {
    setLocalSearch({ roleId: null, keyword: "" });
    setSearchQuery({ page: 0, size: 15 });
    setSelectedRows([]);
    onClose();
  };

  return (
    <Dialog
      open={open}
      maxWidth='md'
      onClose={handleDialogClose}
      PaperComponent={DraggablePaper}
      slotProps={{
        paper: {
          ref: paperRef,
          sx: {
            position: "relative",
            minHeight: "550px",
            minWidth: "750px",
            display: "flex",
            flexDirection: "column",
          },
        },
      }}
    >
      <DialogTitle
        id='draggable-department-user-search-title'
        sx={{
          m: 0,
          p: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          borderBottom: "1px solid",
          borderColor: "divider",
          cursor: "move",
          "&:hover": {
            bgcolor: "action.hover",
          },
        }}
      >
        <Typography variant='h6' component='div'>
          사용자 검색
        </Typography>
        <IconButton
          aria-label='close'
          onClick={handleDialogClose}
          sx={{
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent
        sx={{
          flex: 1,
          p: 2,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <Stack spacing={2}>
          <Box sx={{ display: "flex", gap: 1, pt: 2 }}>
            <TextField
              placeholder='통합 검색어를 입력하세요(최소 2자이상 20자이하)'
              size='small'
              sx={{ flex: 1 }}
              inputProps={{ maxLength: 20 }}
              value={localSearch.keyword ?? ""}
              onChange={(e) => setLocalSearch((prev) => ({ ...prev, keyword: e.target.value }))}
              onKeyDown={handleKeyPress}
              slotProps={{
                input: {
                  endAdornment: (
                    <>
                      {localSearch.keyword && (
                        <InputAdornment position='end'>
                          <IconButton
                            size='small'
                            onClick={() => setLocalSearch((prev) => ({ ...prev, keyword: "" }))}
                            edge='end'
                            tabIndex={-1}
                          >
                            <CloseIcon fontSize='small' />
                          </IconButton>
                        </InputAdornment>
                      )}
                      <InputAdornment position='end'>
                        <span style={{ whiteSpace: "nowrap", fontSize: "0.8rem" }}>
                          {`${localSearch.keyword?.length ?? 0}/20 자`}
                        </span>
                      </InputAdornment>
                    </>
                  ),
                },
              }}
            />
            <Button
              variant='contained'
              sx={{ width: 100, height: 40, bgcolor: "primary.main", "&:hover": { bgcolor: "primary.dark" } }}
              onClick={handleSearchButtonClick}
            >
              검색
            </Button>
          </Box>
          <Box sx={{ height: 351, width: "100%" }}>
            <DataGrid
              rows={rows}
              columns={columns}
              rowCount={data?.pageInfo.total ?? 0}
              checkboxSelection
              rowHeight={48}
              disableRowSelectionOnClick
              disableColumnMenu
              disableColumnSorting
              rowSelectionModel={selectedRows}
              onRowSelectionModelChange={setSelectedRows}
              loading={isFetching}
              pageSizeOptions={[15, 50, 100]}
              paginationMode='server'
              paginationModel={paginationModel}
              onPaginationModelChange={setPaginationModel}
              slots={{
                noRowsOverlay: () => (
                  <GridOverlay>
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        height: "100%",
                        fontSize: "18px",
                        color: "#888",
                        gap: "8px",
                      }}
                    >
                      <InboxIcon sx={{ fontSize: 40, color: "#888" }} />
                      표시할 데이터가 없습니다.
                    </div>
                  </GridOverlay>
                ),
              }}
              sx={{
                "& .MuiDataGrid-main": { overflow: "hidden" },
                "& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within": {
                  outline: "none",
                },
                "& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within": {
                  outline: "none",
                },
                "& .MuiDataGrid-checkboxInput .MuiSvgIcon-root": {
                  fontSize: 20,
                },
              }}
            />
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions
        sx={{
          pb: 3,
          display: "flex",
          justifyContent: "center",
          gap: 1,
        }}
      >
        <Button variant='outlined' onClick={handleDialogClose}>
          취소
        </Button>
        <Button variant='contained' color='primary' onClick={handleApply} disabled={selectedRows.length === 0}>
          등록
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UserSearchDialog;
